import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { getUser } from "./api";

interface AppContextType {
  userId: string;
  setUserId: (value: string) => void;
  userTimeFormat: string;
  setUserTimeFormat: (value: string) => void;
  localTimeZone: string;
  groupName: string;
  setGroupName: (value: string) => void;
  groupLogoUrl: string;
  setGroupLogoUrl: (value: string) => void;
  machineName: string;
  setMachineName: (value: string) => void;
  machineModelType: string;
  setMachineModelType: (value: string) => void;
  isoLocaleCode: string;
  setIsoLocaleCode: (value: string) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [userId, setUserId] = useState<string>("");
  const [userTimeFormat, setUserTimeFormat] =
    useState<string>("yyyy/MM/dd HH:mm");
  const [localTimeZone, setLocalTimeZone] = useState<string>("UTC");
  const [groupName, setGroupName] = useState<string>("");
  const [groupLogoUrl, setGroupLogoUrl] = useState<string>("");
  const [machineName, setMachineName] = useState<string>("");
  const [machineModelType, setMachineModelType] = useState<string>("");
  const [isoLocaleCode, setIsoLocaleCode] = useState<string>("en");

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUser();
        let timeFormat = response?.data?.data?.timeFormat || "yyyy/MM/dd";
        const currentUserId = response?.data?.data?.userId || "";
        const currentIsoLocaleCode =
          response?.data?.data?.isoLocaleCode || "en";

        if (!timeFormat.includes("HH") && !timeFormat.includes("mm")) {
          timeFormat += " HH:mm";
        }

        setUserId(currentUserId);
        setUserTimeFormat(timeFormat);
        setIsoLocaleCode(currentIsoLocaleCode);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();

    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setLocalTimeZone(browserTimeZone);
  }, []);

  return (
    <AppContext.Provider
      value={{
        userId,
        setUserId,
        userTimeFormat,
        setUserTimeFormat,
        localTimeZone,
        groupName,
        setGroupName,
        groupLogoUrl,
        setGroupLogoUrl,
        machineName,
        setMachineName,
        machineModelType,
        setMachineModelType,
        isoLocaleCode,
        setIsoLocaleCode,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
